/**
 * Phase 11 Sprint 6: i18n frontend Studio Web.
 *
 * SCOPE RIDOTTO (PLAN v2 §6): la i18n frontend copre SOLO le superfici pilot
 * ad alto contatto — login/signup screens + onboarding wizard. Il viewer 3D
 * pubblico e l'editor authoring restano IT hardcoded (follow-up Phase 12).
 *
 * Strategia bundle: i18next (la libreria) entra nel bundle studio.js, ma i
 * dizionari lingua (public/i18n/*.json) NON sono nel critical path — vengono
 * `fetch()`-ati a runtime da `/i18n/<locale>.json` (static asset Cloudflare
 * Pages). Si carica SOLO la lingua negoziata, non tutte e 5. Questo soddisfa
 * il requisito PLAN "il bundle lingua non deve essere nel critical path".
 *
 * Negoziazione client-side (mirror del backend src/i18n/locale.ts, priorita'
 * ridotta lato client che non ha user/studio):
 *   cookie `oniri_locale` > navigator.language > 'it'
 *
 * Il backend negozia con priorita' piu' ricca (cookie > user.preferred_locale
 * > studio.default_locale > Accept-Language); il frontend, dopo che l'utente
 * cambia lingua via selettore, fa PATCH /api/me/preferences E setta il cookie,
 * cosi' le due negoziazioni restano coerenti.
 */
import i18next from 'i18next';

export const SUPPORTED_LOCALES = ['it', 'en', 'fr', 'de', 'es'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'it';
export const LOCALE_COOKIE = 'oniri_locale';

/** Valida una stringa unknown contro il set dei locale supportati. */
export function parseLocale(value: unknown): Locale | null {
  if (typeof value !== 'string') return null;
  return (SUPPORTED_LOCALES as readonly string[]).includes(value) ? (value as Locale) : null;
}

/** Legge il cookie `oniri_locale` da document.cookie. */
export function readLocaleCookie(): Locale | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const [name, ...rest] = part.trim().split('=');
    if (name === LOCALE_COOKIE) {
      return parseLocale(decodeURIComponent(rest.join('=')));
    }
  }
  return null;
}

/**
 * Scrive il cookie `oniri_locale`. SameSite=Lax + 1 anno: e' una preferenza
 * UI per-device, non un dato sensibile. NON HttpOnly (il JS lo deve leggere).
 *
 * NOTA lint (noDocumentCookie warning): biome suggerisce la Cookie Store API,
 * ma e' async e non supportata su Safari/Firefox. Per una semplice preferenza
 * UI non sensibile, `document.cookie` sincrono e' la scelta corretta e KISS —
 * il warning e' accettato consapevolmente.
 */
export function writeLocaleCookie(locale: Locale): void {
  if (typeof document === 'undefined') return;
  const oneYear = 60 * 60 * 24 * 365;
  document.cookie = `${LOCALE_COOKIE}=${encodeURIComponent(locale)}; path=/; max-age=${oneYear}; SameSite=Lax`;
}

/**
 * Negozia il locale iniziale: cookie > navigator.language > DEFAULT_LOCALE.
 * navigator.language puo' essere un tag regionale (es. 'fr-FR') → tentiamo
 * anche il primo segmento.
 */
export function negotiateLocale(): Locale {
  const fromCookie = readLocaleCookie();
  if (fromCookie !== null) return fromCookie;

  if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
    const tag = navigator.language.toLowerCase();
    const direct = parseLocale(tag);
    if (direct !== null) return direct;
    const primary = parseLocale(tag.split('-')[0] ?? '');
    if (primary !== null) return primary;
  }

  return DEFAULT_LOCALE;
}

/**
 * Carica il dizionario di un locale da `/i18n/<locale>.json`. Lazy: chiamato
 * solo per la lingua attiva. Se la fetch fallisce ritorna `{}` — i18next
 * ripieghera' sul fallback EN gia' caricato, e in ultima istanza t() ritorna
 * la chiave. Nessun fallback silenzioso che maschera errori: logghiamo warn.
 */
async function fetchDictionary(locale: Locale): Promise<Record<string, string>> {
  try {
    const res = await fetch(`/i18n/${locale}.json`, { credentials: 'same-origin' });
    if (!res.ok) {
      console.warn(`[i18n] fetch /i18n/${locale}.json fallita: HTTP ${res.status}`);
      return {};
    }
    return (await res.json()) as Record<string, string>;
  } catch (err) {
    console.warn(`[i18n] fetch /i18n/${locale}.json errore:`, err);
    return {};
  }
}

let initialized = false;
let currentLocale: Locale = DEFAULT_LOCALE;

/**
 * Inizializza i18next per la lingua negoziata. Carica SEMPRE anche EN come
 * fallback bundle (i dizionari fr/de/es sono copie di en finche' Phase 12 non
 * porta le traduzioni native — il fallback EN garantisce comunque coerenza se
 * un .json e' parziale). Idempotente: la seconda chiamata e' un no-op.
 *
 * @returns il Locale effettivamente attivo dopo l'init.
 */
export async function initI18n(): Promise<Locale> {
  if (initialized) return currentLocale;

  const locale = negotiateLocale();
  currentLocale = locale;

  // Carica il dizionario della lingua attiva + EN come fallback. Se la lingua
  // attiva e' gia' 'en' evitiamo la doppia fetch.
  const [activeDict, enDict] =
    locale === 'en'
      ? await Promise.all([fetchDictionary('en'), Promise.resolve({})])
      : await Promise.all([fetchDictionary(locale), fetchDictionary('en')]);

  await i18next.init({
    lng: locale,
    fallbackLng: 'en',
    // Chiavi flat dot-notation: disabilitiamo i separatori di namespace/nesting
    // cosi' 'login.title' e' UNA chiave, non un path annidato.
    keySeparator: false,
    nsSeparator: false,
    interpolation: {
      // Le stringhe sono inserite via setText/textContent (XSS-safe):
      // l'escaping HTML di i18next non serve e romperebbe caratteri come `'`.
      escapeValue: false,
      // Allinea la sintassi placeholder al backend: `{var}` invece di `{{var}}`.
      prefix: '{',
      suffix: '}',
    },
    resources: {
      [locale]: { translation: activeDict },
      ...(locale === 'en' ? {} : { en: { translation: enDict } }),
    },
  });

  initialized = true;
  return currentLocale;
}

/** Locale attualmente attivo (dopo initI18n). */
export function getLocale(): Locale {
  return currentLocale;
}

/**
 * Helper di traduzione. Mirror dell'API backend `t(key, vars)`.
 * Prima di initI18n() ritorna la chiave stessa (no crash) — i componenti
 * pilot vengono montati dopo l'await initI18n() nel bootstrap.
 *
 * @param key  Chiave flat dot-notation (es. 'login.submit').
 * @param vars Valori di interpolazione per i placeholder `{nome}`.
 */
export function t(key: string, vars?: Record<string, string | number>): string {
  if (!initialized) return key;
  return i18next.t(key, vars ?? {}) as string;
}
